import { Injectable, NgZone } from '@angular/core';
import Swal from 'sweetalert2';
import { ToastrService } from "ngx-toastr";
import { AuthService } from 'src/app/Auth/service/auth.service';

@Injectable({
  providedIn: 'root'
})
export class ActivityService {
  private timeoutId: any;
  private curentLang: string = '';
  private readonly TIMEOUT_LIMIT = 24 * 60 * 60 * 1000;        // 1 day
  private remainingTimeout = 89 * 24 * 60 * 60 * 1000;          // 89 days

  /** Throttle: only act on activity once per 5 s to avoid hammering change detection. */
  private lastReset = 0;
  private readonly THROTTLE_MS = 5_000;

  constructor(
    private toastr: ToastrService,
    private authService: AuthService,
    private ngZone: NgZone,
  ) {
    this.curentLang = localStorage.getItem('curentLang') || '';
    this.initEventListeners();
  }

  /**
   * All listeners run OUTSIDE Angular's zone so they never trigger change detection.
   * We only re-enter the zone when we actually need to log the user out.
   */
  private initEventListeners(): void {
    this.ngZone.runOutsideAngular(() => {
      const onActivity = (): void => this.checkTokenAndResetTimer();
      window.addEventListener('mousemove',   onActivity, { passive: true });
      window.addEventListener('mousedown',   onActivity, { passive: true });
      window.addEventListener('keypress',    onActivity, { passive: true });
      window.addEventListener('touchmove',   onActivity, { passive: true });
      window.addEventListener('beforeunload', () => this.setLastActivityTime());
    });
  }

  private checkTokenAndResetTimer(): void {
    if (!localStorage.getItem('token')) {
      return;
    }
    const now = Date.now();
    if (now - this.lastReset < this.THROTTLE_MS) {
      return;
    }
    this.lastReset = now;
    this.resetTimer();
  }

  private setLastActivityTime(): void {
    localStorage.setItem('lastActivityTime', Date.now().toString());
  }

  private resetTimer(): void {
    this.setLastActivityTime();

    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    if (this.remainingTimeout > this.TIMEOUT_LIMIT) {
      this.timeoutId = setTimeout(() => {
        this.remainingTimeout -= this.TIMEOUT_LIMIT;
        this.resetTimer();
      }, this.TIMEOUT_LIMIT);
    } else {
      this.timeoutId = setTimeout(() => {
        this.ngZone.run(() => this.logout());
      }, this.remainingTimeout);
    }
  }

  private logout(): void {
    const Toast = Swal.mixin({
      toast: true,
      position: 'top',
      showConfirmButton: false,
      timer: 2000,
      timerProgressBar: true,
      color: 'red',
      backdrop: true,
      didOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer);
        toast.addEventListener('mouseleave', Swal.resumeTimer);
      },
      willClose: () => {
        void this.authService.endSession({ redirectTo: '/login', useLocation: true });
      },
    });

    Toast.fire({
      icon: 'warning',
      title:
        this.curentLang === 'ar'
          ? 'تم مسح بيانات الجلسة بسبب عدم النشاط.'
          : this.curentLang === 'en'
            ? 'Session data cleared due to inactivity.'
            : 'נמחקו נתוני המושב עקב חוסר פעילות.',
    });
  }

  public checkInactivity(): void {
    const lastActivityTime = localStorage.getItem('lastActivityTime');
    if (lastActivityTime && localStorage.getItem('token')) {
      const timeDiff = Date.now() - parseInt(lastActivityTime, 10);
      if (timeDiff >= this.remainingTimeout) {
        this.ngZone.run(() => this.logout());
      }
    }
  }
}
